import React, { useState, useEffect } from "react";
import NotificationContainer from "../Notification.tsx";
import { useUserContext } from "../../context/UserContext.tsx";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "../Toast/toast.ts";
import StoresDropDown from "../GlobalStoresDropDown.tsx";
import ThemeSwitcher from "../ThemeSwitcher/index.tsx";
import SettingsMenu from "./SettingsMenu.tsx";

interface HeaderProps {
  darkMode: boolean;
  toggleDarkMode: () => void;
}

const routeMeta: Record<string, { label: string; title: string; subtitle: string }> = {
  "/home": { label: "OVERVIEW", title: "Store Intelligence Dashboard", subtitle: "POS, video, people and incident intelligence in one place." },
  "/live": { label: "LIVE VIEW", title: "Live View", subtitle: "Real-time camera grid for active monitoring." },
  "/cashier-fraud": { label: "Cashier Reports", title: "POS Loss Prevention", subtitle: "Monitor cashier exceptions and fraud patterns." },
  "/event-search": { label: "EVENT SEARCH", title: "Event Search", subtitle: "Search POS, video, AI and monitoring events." },
  "/transactions": { label: "TRANSACTIONS", title: "Transactions", subtitle: "Search receipts, POS events and linked video." },
  "/report-summary": { label: "REPORTS", title: "Reports Analytics", subtitle: "Daily reports across locations." },
  "/people-analytics": { label: "PEOPLE", title: "People Analytics", subtitle: "Visitor traffic and conversion insights." },
  "/cashier-analytics": { label: "CASHIER", title: "Cashier Analytics", subtitle: "Risk score from exceptions and transaction behavior." },
  "/register-analytics": { label: "REGISTER", title: "Register Analytics", subtitle: "Sales and exceptions by register." },
  "/ai-detection": { label: "AI DETECTION HUB", title: "AI Detection Hub", subtitle: "Safety, security and behavioural events." },
  "/client-query": { label: "Client Queries", title: "Queries Center", subtitle: "Track evidence request and custom request queries." },
  "/reports": { label: "REPORTS", title: "Reports", subtitle: "Daily summary, weekly LP, cashier risk and monthly review." },
  "/transaction-alert": { label: "ALERTS & RULES", title: "Transaction Alerts", subtitle: "Manage alert rules and notifications." },
  "/trigger-alerts": { label: "ALERTS & RULES", title: "Trigger Alerts", subtitle: "Configure trigger-based alert rules." },
  "/profile": { label: "SETTINGS", title: "Profile", subtitle: "Manage your account details." },
  "/change-pwd": { label: "SETTINGS", title: "Change Password", subtitle: "Update your account password." },
  "/manage-stores": { label: "SETTINGS", title: "Manage Stores", subtitle: "Add, edit or remove store locations." },
  "/contact-us": { label: "CONTACT", title: "Contact Us", subtitle: "Get in touch with our support team." },
  "/devices": { label: "DEVICES", title: "Devices", subtitle: "Monitor device health and connectivity." },
  "/faq": { label: "FAQ", title: "FAQ", subtitle: "Frequently asked questions." },
};

const Header: React.FC<HeaderProps> = ({ darkMode, toggleDarkMode }) => {
  const {
    setUserDetails,
    reload,
    handleTokenExpired,
    setStoreInfoMap,
    storeInfoMap,
    globalstorevalue,
    setPosInfo,
    activeRemoteUrl,
    setCamerasInfo,
  } = useUserContext();

  const location = useLocation();

  const [userData, setUserData] = useState<any>(null);
  const storedToken = localStorage.getItem("token");

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (storedToken) {
          // Fetch user data
          const userResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/cust-portal/customer/user-details`,
            {
              headers: {
                Authorization: `Bearer ${storedToken}`,
              },
            }
          );
          setUserData(userResponse?.data);
          setUserDetails(userResponse?.data);
          let map: Record<string, { remoteUrl: string; storeData: any }> = {};
          if (userResponse?.data?.stores) {
            userResponse.data.stores.forEach((store: any) => {
              if (store?.configs?.visen_ai?.remote_url) {
                map[store._id] = {
                  remoteUrl: store.configs.visen_ai.remote_url,
                  storeData: store,
                };
              }
            });
          }
          setStoreInfoMap(map);
        }
      } catch (error: any) {
        // if (error.response && error.response.status === 400) {
        //     const { message } = error.response.data;
        //     if (message === "Token Expired") {
        //         toast.error("Your session has expired. Please log in again.");
        //         localStorage.clear();
        //         navigate("/");
        //         return;
        //     }
        // }
        const errorMessage = error?.response?.data?.message || error?.message || "An error occurred. Please try again.";
        toast.error(errorMessage);
        if (errorMessage === "Token Expired") {
          handleTokenExpired();
        }
      }
    };

    fetchUserData();
    // eslint-disable-next-line
  }, [reload, storedToken]);

  useEffect(() => {
    if (!storeInfoMap || Object.keys(storeInfoMap).length === 0) return;

    const getPOS = async () => {
      try {
        setPosInfo([]);
        if (!activeRemoteUrl) return;

        response = await axios.get(`${activeRemoteUrl}/v1/pos`);

        if (response?.data?.success?.pos) {
          setPosInfo(response.data.success.pos);
        } else {
          setPosInfo([]);
        }
      } catch (error) {
        console.error("Error fetching POS IDs:", error);
        setPosInfo([]);
      }
    };

    getPOS();
  }, [globalstorevalue, activeRemoteUrl]);

  useEffect(() => {
    if (!storeInfoMap || Object.keys(storeInfoMap).length === 0) return;

    const getCamerasInfo = async () => {
      try {
        setCamerasInfo([]);
        if (!activeRemoteUrl) return;
        const response = await axios.get(`${activeRemoteUrl}/v1/cameras/ids`);
        if (response?.data?.success?.ids) {
          setCamerasInfo(response.data.success.ids);
        } else {
          setCamerasInfo([]);
        }
      } catch (error) {
        console.error("Error fetching Cameras IDs:", error);
        setCamerasInfo([]);
      }
    };

    getCamerasInfo();
  }, [activeRemoteUrl]);

  // Get current page meta
  const currentMeta = routeMeta[location.pathname] || {
    label: "VISEN",
    title: "Dashboard",
    subtitle: "",
  };

  return (
    <header className="bg-[rgb(var(--background))] border-b border-[rgb(var(--border-primary))] mb-7">
      {/* Top row: page info left, controls right */}
      <div className="flex flex-col md:flex-row md:items-center justify-between pt-6 md:pt-8 pb-4 md:pb-6 gap-3">
        {/* Left: Page title area */}
        <div className="flex flex-col gap-1.5">
          <span className="page-breadcrumb">
            VISEN · {currentMeta.label}
          </span>
          <h1 className="page-title m-0">
            {currentMeta.title}
          </h1>
          {currentMeta.subtitle && (
            <p className="page-subtitle m-0 -mt-0.5">
              {currentMeta.subtitle}
            </p>
          )}
        </div>

        {/* Right: Controls */}
        <div className="flex items-center gap-2 md:gap-3 flex-wrap">
          <ThemeSwitcher
            darkMode={darkMode}
            toggleDarkMode={toggleDarkMode}
          />

          {/* Search bar */}
          {/* <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-md border border-[rgb(var(--border-secondary))] bg-[rgb(var(--surface-2))] min-w-[200px] lg:min-w-[260px]">
            <Search size={14} className="text-[rgb(var(--text-muted))] shrink-0" />
            <input
              type="text"
              placeholder="Search events, receipts, cashier..."
              className="bg-transparent border-none outline-none text-body-sm placeholder:text-[rgb(var(--text-muted))] w-full"
            />
          </div> */}

          {/* Scope / Store selector */}
          <StoresDropDown />


          {/* Export Report button */}
          {/* <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gold-2 hover:bg-gold-3 text-white text-body-sm font-medium transition-colors whitespace-nowrap">
            <Download size={13} />
            Export Report
          </button> */}

          {/* Notifications */}
          <div className="relative">
            <NotificationContainer />
          </div>

          {/* Avatar / User Menu */}
          <SettingsMenu userData={userData} />
        </div>
      </div>
    </header>
  );
};

export default Header;
